import React from "react";
import { PrimaryButton } from "@ucl/library";
import { AppToasterV2, dialogStore } from "@ucl/library";
import { ShareApplicationPerson } from "@/types/ShareApplication";
import { useSendInviteEmail } from "./useSendInviteEmail";
import "./InviteEmailButton.scss";

export interface InviteEmailButtonProps {
  person: ShareApplicationPerson;
  financialApplicationId?: string;
  buttonText: string;
  isReSend?: boolean;
}

export const InviteEmailButton: React.FC<InviteEmailButtonProps> = ({
  person,
  financialApplicationId,
  buttonText,
  isReSend = false,
}) => {
  const { sendInviteEmail, isSending, error, success } = useSendInviteEmail();

  // Determine if button should be disabled
  const isDisabled = isSending || !financialApplicationId || !person.validInviteId || !person.isEmailValid;

  // Get tooltip text
  const getTooltipText = (): string | undefined => {
    if (!financialApplicationId) {
      return "Financial application ID is required";
    }
    if (!person.validInviteId) {
      return "No valid invite ID available";
    }
    if (!person.isEmailValid) {
      return "Invalid email address format";
    }
    return undefined;
  };

  const handleSendInviteEmail = async () => {
    if (!financialApplicationId) {
      console.error("Financial application ID is required");
      return;
    }

    if (!person.validInviteId) {
      console.error("No valid invite ID available for this person");
      return;
    }

    if (!person.isEmailValid) {
      console.error("Invalid email address");
      return;
    }

    const dialogId = `send-invite-email-${person.validInviteId}`;
    const actionText = isReSend ? "re-send" : "send";

    dialogStore.openConfirmDialog({
      dialogId,
      headerText: `${isReSend ? "Re-send" : "Send"} Invite Email`,
      content: `Are you sure you want to ${actionText} an invite email to ${person.fullName} at ${person.emailAddress}?`,
      confirmButtonText: isReSend ? "Re-send Email" : "Send Email",
      // color: "forest-green",
      onConfirm: async () => {
        const result: { isSuccess: boolean; error: any } = {
          isSuccess: true,
          error: undefined,
        };

        try {
          await sendInviteEmail(financialApplicationId, person);
          dialogStore.closeDialog(dialogId);
          AppToasterV2.showSuccessAppToaster(`Invite email ${actionText} successfully!`);
        } catch (err) {
          console.error("Failed to send invite email:", err);
          result.isSuccess = false;
          result.error = err;
        }

        return result;
      },
    });
  };

  return (
    <div className="invite-email-button">
      {error && (
        <div className="invite-email-button__error">
          <p>Error: {error}</p>
        </div>
      )}

      {success && (
        <div className="invite-email-button__success">
          <p>Invite email sent successfully!</p>
        </div>
      )}

      <PrimaryButton
        text={isSending ? "Sending..." : buttonText}
        onClick={handleSendInviteEmail}
        disabled={isDisabled}
        title={getTooltipText()}
      />
    </div>
  );
};

export default InviteEmailButton;
